import React, {useState, useEffect} from 'react';
import {useLocation, useHistory} from 'react-router-dom';
import {API} from 'aws-amplify';
import queryString from 'query-string';
import moment from 'moment-timezone';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faXmark, faLink, faEllipsisHAlt} from '@fortawesome/pro-light-svg-icons';

import AuthService from '../auth/services/AuthService';
import PrimaryAppBar from '../lesson-builder/components/PrimaryAppBar';
import ShareDialog from '../lesson-builder/components/ShareDialog';
import schoolBook from '../word-work/images/clipart/school/books.png';

import styles from '../css/StudentDashboard.module.css';
import baseStyles from '../css/Base.module.css';
import {LESSON, APP_TITLES, ENDPOINT} from '../constants';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function StudentDashboard() {
  const location = useLocation();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [student, setStudent] = useState(null);
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const [isMenuOpen, toggleMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isShareDialogOpen, toggleShareDialog] = useState(false);
  const [shareURL, setShareURL] = useState('');

  const queryStrings = queryString.parse(location.search);
  const authCode = queryStrings.authCode;

  useEffect(() => {
    if (authCode) {
      const fetchAssignments = async () => {
        const {isAuthenticated} = await AuthService.checkAuthentication();
        setIsAuthenticated(isAuthenticated);

        const student = await API.get(ENDPOINT, `/students/auth-code/${authCode}`);
        setStudent(student);

        const assignments = await API.get(ENDPOINT, `/assignments/auth-code/${authCode}`);
        setAssignments(assignments);

        // A pending, first assignment in a session
        if (!isAuthenticated) {
          const pendingAssignments = await API.get(
            ENDPOINT,
            `/assignments/auth-code/${authCode}?progress=PENDING&sessionIndex=0`,
          );
          if (pendingAssignments.length > 0) setPendingAssignment(pendingAssignments[0]);
        }

        setIsLoading(false);
      };
      fetchAssignments();
    }
  }, [authCode]);

  function formatAssignmentType(type) {
    if (type === LESSON) return LESSON;
    else return `${APP_TITLES[type].toUpperCase()} EXERCISE`;
  }

  function formatProgressTitle(progress) {
    if (progress === 'PENDING') return 'NOT STARTED';
    else if (progress === 'IN_PROGRESS') return 'STARTED';
    else if (progress === 'COMPLETED') return 'COMPLETED';
    else return 'UNKNOWN';
  }

  function handleClickMenu(event, assignment) {
    setShareURL(
      `${window.location.origin}/assignment?authCode=${assignment.student.authCode}&sessionID=${assignment.session.id}&sessionIndex=${assignment.session.index}`,
    );
    setAnchorEl(event.currentTarget);
    toggleMenu(true);
    event.stopPropagation();
  }

  function handleClickGetShareLink() {
    toggleMenu(false);
    toggleShareDialog(true);
  }

  function renderMenu() {
    if (isAuthenticated && anchorEl) {
      return (
        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={() => toggleMenu(false)}>
          <MenuItem onClick={handleClickGetShareLink}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faLink} />
            </ListItemIcon>
            <ListItemText primary="Get Link" />
          </MenuItem>
        </Menu>
      );
    }
  }

  function renderEmpty() {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px dashed black',
          borderRadius: 4,
          borderColor: 'rgba(0, 0, 0, 0.12)',
        }}
        className="m-10"
      >
        <Typography variant="subtitle1" className="m-tb-20">
          No Assignments
        </Typography>
      </div>
    );
  }

  function renderActionButton(assignment) {
    if (isAuthenticated) {
      return (
        <IconButton onClick={(event) => handleClickMenu(event, assignment)}>
          <FontAwesomeIcon icon={faEllipsisHAlt} />
        </IconButton>
      );
    } else {
      const assignmentURL = `/assignment?authCode=${assignment.student.authCode}&sessionID=${assignment.session.id}&sessionIndex=${assignment.session.index}`;
      return <Button onClick={() => history.push(assignmentURL)}>START</Button>;
    }
  }

  function renderAssignments(progress) {
    const filteredAssignments = assignments.filter((assignment) => assignment.progress === progress);
    return (
      <Grid xs={4} key={progress}>
        <Paper sx={{padding: 2}}>
          <List>
            <ListItem>
              <ListItemText
                primary={`${formatProgressTitle(progress)} (${filteredAssignments.length})`}
                primaryTypographyProps={{variant: 'h5', className: 'fnt-bold'}}
              />
            </ListItem>
          </List>
          <Divider />
          {filteredAssignments.length > 0 ? (
            <List>
              {filteredAssignments.map((assignment) => (
                <React.Fragment key={assignment.id}>
                  <ListItem secondaryAction={renderActionButton(assignment)}>
                    <ListItemText
                      primary={assignment.assignment.name}
                      secondary={
                        <React.Fragment>
                          <Typography sx={{display: 'block'}} component="span" variant="body2">
                            {moment(assignment.createdAt).format('MMM D, YYYY')}
                          </Typography>
                          <Typography sx={{display: 'block'}} component="span" variant="body2">
                            {formatAssignmentType(assignment.type)}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          ) : (
            renderEmpty()
          )}
        </Paper>
      </Grid>
    );
  }

  function renderPendingAssignmentDialog() {
    const pendingAssignmentURL = `/assignment?authCode=${pendingAssignment?.student?.authCode}&sessionID=${pendingAssignment?.session?.id}&auto=true`;
    return (
      <Dialog
        fullScreen
        open={pendingAssignment !== null}
        onClose={() => setPendingAssignment(null)}
        TransitionComponent={Transition}
      >
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => setPendingAssignment(null)} aria-label="close">
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Toolbar>
        <div
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <img src={schoolBook} alt="books" />
          <Typography variant="h4" className="fnt-bold m-t-20 m-b-10">
            You have new assignments!
          </Typography>
          <Typography variant="h5" className="m-b-20">
            Your teacher has some fun lessons for you to complete.
          </Typography>

          <Fab onClick={() => history.push(pendingAssignmentURL)} size="large" className={styles.fabRoot}>
            <Typography className="clr-white fnt-bold">START</Typography>
          </Fab>

          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faXmark} />}
            onClick={() => setPendingAssignment(null)}
          >
            I&apos;LL DO THIS LATER
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <>
      <ShareDialog open={isShareDialogOpen} handleClose={() => toggleShareDialog(false)} shareURL={shareURL} />
      {renderPendingAssignmentDialog()}
      {renderMenu()}
      {isAuthenticated ? (
        <PrimaryAppBar />
      ) : (
        <AppBar position="sticky" className="bg-brandorange">
          <Toolbar variant="dense">
            <Typography variant="h6" className={baseStyles['f-g-1']}>
              Flamingo Learning
            </Typography>
          </Toolbar>
        </AppBar>
      )}
      <div className="p-20">
        {isLoading && (
          <div className={baseStyles['progress-container']}>
            <CircularProgress />
          </div>
        )}
        {!isLoading && (
          <>
            <Grid container spacing={2}>
              <Grid xs={6}>
                <Typography variant="h4" className="fnt-thin">
                  Welcome
                </Typography>
                <Typography variant="h4" className="fnt-bold">
                  {student?.firstName}
                </Typography>
              </Grid>
              <Grid xs={6}>
                <Typography variant="h4" className="fnt-thin">
                  Your join code is
                </Typography>
                <Typography variant="h4" className="fnt-bold">
                  {student?.authCode}
                </Typography>
              </Grid>
              {isAuthenticated && (
                <Grid xs={12}>
                  <Paper sx={{padding: 2}}>
                    <Typography variant="h5" className="fnt-medium">
                      {assignments.length} Assignments
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
            <Grid container spacing={2} className="m-t-20">
              {['PENDING', 'IN_PROGRESS', 'COMPLETED'].map((progress) => renderAssignments(progress))}
            </Grid>
          </>
        )}
      </div>
    </>
  );
}
